import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { PermissionGate } from "../auth/access";
import { describeError, useToast } from "../components/toast";
import type { ActionKey } from "../components/toast/ToastProvider";
import CommitDrawer from "../components/CommitDrawer";
import AccessTimeIcon from "@mui/icons-material/AccessTimeOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DAYS_OF_WEEK,
  type DayOfWeek,
  type PmsConfig,
} from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { PageHeader } from "../components/PageHeader";
import { dayLabel } from "../components/OpeningHoursFields";
import { useFormat } from "../i18n/format";
import { errorText } from "../utils/apiError";
import LinkConnectionsPanel from "../components/link/LinkConnectionsPanel";

interface Props {
  premiseId: string;
}

function emptyConfig(): PmsConfig {
  return {
    vendor: null,
    dailyReset: false,
    scheduledCheckInOut: {
      enabled: false,
      schedule: {},
    },
  };
}

/** Day entries with the empty/undefined keys stripped, as the server stores them. */
function cleanDay(entry: { checkIn?: string; checkOut?: string }) {
  const cleaned: { checkIn?: string; checkOut?: string } = {};
  if (entry.checkIn !== undefined) cleaned.checkIn = entry.checkIn;
  if (entry.checkOut !== undefined) cleaned.checkOut = entry.checkOut;
  return cleaned;
}

export default function PmsConfigurationPage({ premiseId }: Props) {
  // The saved PMS config. Nothing on this page is "pending": the switches save
  // the moment they flip, and the schedule is edited in a drawer whose Save
  // writes it. There is no page-level Save.
  const [config, setConfig] = useState<PmsConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const toast = useToast();
  const { t } = useTranslation("premises");
  const fmt = useFormat();
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    foyerApi
      .getPmsConfig(premiseId)
      .then((cfg) => {
        if (cancelled) return;
        setConfig({ ...emptyConfig(), ...cfg });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(errorText(e, t));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [premiseId, t]);

  // Quick successive changes must not overwrite each other: each one waits for
  // the previous PUT and is applied to what that PUT returned.
  const latest = useRef<PmsConfig | null>(null);
  latest.current = config;
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  const persist = useCallback(
    (
      update: (c: PmsConfig) => PmsConfig,
      messages: { success: string; failure: ActionKey }
    ): Promise<boolean> => {
      const run = async (): Promise<boolean> => {
        const base = latest.current;
        if (!base) return false;
        try {
          const persisted = await foyerApi.putPmsConfig(
            premiseId,
            update(base)
          );
          const next = { ...emptyConfig(), ...persisted };
          latest.current = next;
          setConfig(next);
          toast.success(messages.success);
          return true;
        } catch (e) {
          toast.error(describeError(t, messages.failure, e));
          return false;
        }
      };
      const result = queue.current.then(run, run);
      queue.current = result;
      return result;
    },
    [premiseId, t, toast]
  );

  async function runBulk(kind: "in" | "out") {
    setBulkBusy(true);
    try {
      const res =
        kind === "in"
          ? await foyerApi.checkInAll(premiseId)
          : await foyerApi.checkOutAll(premiseId);
      toast.success(
        kind === "in"
          ? t("pmsConfigurationPage.checkedIn", { count: res.changed })
          : t("pmsConfigurationPage.checkedOut", { count: res.changed })
      );
    } catch (e) {
      toast.error(
        describeError(
          t,
          kind === "in" ? "checkInAllUnits" : "checkOutAllUnits",
          e
        )
      );
    } finally {
      setBulkBusy(false);
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !config) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          {error ?? t("pmsConfigurationPage.loadFailed")}
        </Alert>
      </Box>
    );
  }

  const schedEnabled = config.scheduledCheckInOut?.enabled ?? false;
  const schedule = config.scheduledCheckInOut?.schedule ?? {};
  const scheduledDays = DAYS_OF_WEEK.filter(
    (d) =>
      schedule[d]?.checkIn !== undefined || schedule[d]?.checkOut !== undefined
  );

  return (
    <Stack spacing={3} data-testid="pms-config-page">
      <PageHeader title={t("pmsConfigurationPage.title")} />

      {/* ⚠️ The hardcoded five-vendor picker that used to live here is GONE
          (INT-11). It offered Protel, Apaleo and Cloudbeds while the folio seam
          knew only manual/mews/opera, so selecting one stored a free string no
          seam could act on and produced no refusal at all — the exact failure
          `folioPostingProviders.ts` was written to close. The panel below has
          no vendor list of its own: it renders the adapter registry. */}
      <LinkConnectionsPanel premiseId={premiseId} />

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <FormControlLabel
              control={
                <PermissionGate
                  resource="pms-link"
                  level="administrate"
                  deniedProps={{ disabled: true }}
                >
                  <Switch
                    checked={!!config.dailyReset}
                    onChange={(e) => {
                      const on = e.target.checked;
                      void persist(
                        (c) => ({
                          ...c,
                          dailyReset: on,
                          // Seed a sensible default the first time it's enabled.
                          dailyResetTime:
                            on && !c.dailyResetTime
                              ? "00:00"
                              : c.dailyResetTime,
                        }),
                        {
                          success: on
                            ? t("pmsConfigurationPage.dailyResetOn")
                            : t("pmsConfigurationPage.dailyResetOff"),
                          failure: on
                            ? "enableDailyUnitReset"
                            : "disableDailyUnitReset",
                        }
                      );
                    }}
                    data-testid="pms-daily-reset"
                  />
                </PermissionGate>
              }
              label={t("pmsConfigurationPage.dailyReset")}
            />
            {config.dailyReset && (
              <Typography variant="body2" data-testid="pms-daily-reset-summary">
                {t("pmsConfigurationPage.dailyResetSummary", {
                  time: config.dailyResetTime ?? "00:00",
                })}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {t("pmsConfigurationPage.dailyResetHelp")}
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <FormControlLabel
              control={
                <PermissionGate
                  resource="pms-link"
                  level="administrate"
                  deniedProps={{ disabled: true }}
                >
                  <Switch
                    checked={schedEnabled}
                    onChange={(e) => {
                      const on = e.target.checked;
                      void persist(
                        (c) => ({
                          ...c,
                          scheduledCheckInOut: {
                            enabled: on,
                            schedule: c.scheduledCheckInOut?.schedule ?? {},
                          },
                        }),
                        {
                          success: on
                            ? t("pmsConfigurationPage.autoCheckInOutOn")
                            : t("pmsConfigurationPage.autoCheckInOutOff"),
                          failure: on
                            ? "enableAutoCheckInOut"
                            : "disableAutoCheckInOut",
                        }
                      );
                    }}
                    data-testid="pms-sched-enabled"
                  />
                </PermissionGate>
              }
              label={t("pmsConfigurationPage.enableAutoCheckInOut")}
            />
            <Typography variant="caption" color="text.secondary">
              {t("pmsConfigurationPage.timesNote")}
            </Typography>
            <Box sx={{ opacity: schedEnabled ? 1 : 0.5 }}>
              {scheduledDays.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("pmsConfigurationPage.noDaysScheduled")}
                </Typography>
              ) : (
                <Stack spacing={0.25} data-testid="pms-schedule-summary">
                  {scheduledDays.map((day) => {
                    const checkIn = schedule[day]?.checkIn;
                    const checkOut = schedule[day]?.checkOut;
                    const times =
                      checkIn !== undefined && checkOut !== undefined
                        ? t("pmsConfigurationPage.checkInAndOut", {
                            checkIn,
                            checkOut,
                          })
                        : checkIn !== undefined
                          ? t("pmsConfigurationPage.checkInAt", {
                              time: checkIn,
                            })
                          : t("pmsConfigurationPage.checkOutAt", {
                              time: checkOut,
                            });
                    return (
                      <Typography key={day} variant="body2">
                        {t("pmsConfigurationPage.daySchedule", {
                          day: dayLabel(fmt, day),
                          times,
                        })}
                      </Typography>
                    );
                  })}
                </Stack>
              )}
            </Box>
            <Box>
              <PermissionGate resource="pms-link" level="administrate">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setEditingSchedule(true)}
                  data-testid="pms-edit-schedule"
                >
                  {t("pmsConfigurationPage.editSchedule")}
                </Button>
              </PermissionGate>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1">
              {t("pmsConfigurationPage.bulkTitle")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("pmsConfigurationPage.bulkHelp")}
            </Typography>
            <Stack direction="row" spacing={1}>
              <PermissionGate resource="units" level="edit">
                <Button
                  variant="outlined"
                  disabled={bulkBusy}
                  onClick={() => runBulk("in")}
                  data-testid="pms-bulk-checkin"
                >
                  {t("pmsConfigurationPage.checkInAll")}
                </Button>
              </PermissionGate>
              <PermissionGate resource="units" level="edit">
                <Button
                  variant="outlined"
                  disabled={bulkBusy}
                  onClick={() => runBulk("out")}
                  data-testid="pms-bulk-checkout"
                >
                  {t("pmsConfigurationPage.checkOutAll")}
                </Button>
              </PermissionGate>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {editingSchedule && (
        <PmsScheduleDrawer
          config={config}
          onClose={() => setEditingSchedule(false)}
          onSave={(next) =>
            persist(
              (c) => ({
                ...c,
                dailyResetTime: next.dailyResetTime ?? c.dailyResetTime,
                scheduledCheckInOut: {
                  enabled: c.scheduledCheckInOut?.enabled ?? false,
                  schedule: next.schedule,
                },
              }),
              {
                success: t("pmsConfigurationPage.scheduleUpdated"),
                failure: "savePmsSchedule",
              }
            )
          }
        />
      )}
    </Stack>
  );
}

/** The reset time and the per-day check-in/out table, saved together. */
function PmsScheduleDrawer({
  config,
  onClose,
  onSave,
}: {
  config: PmsConfig;
  onClose: () => void;
  onSave: (next: {
    dailyResetTime: string | undefined;
    schedule: NonNullable<PmsConfig["scheduledCheckInOut"]>["schedule"];
  }) => Promise<boolean>;
}) {
  const { t } = useTranslation("premises");
  const fmt = useFormat();
  const [schedule, setSchedule] = useState(() => ({
    ...(config.scheduledCheckInOut?.schedule ?? {}),
  }));
  const [resetTime, setResetTime] = useState(config.dailyResetTime ?? "00:00");
  const schedEnabled = config.scheduledCheckInOut?.enabled ?? false;

  function setDay(
    day: DayOfWeek,
    field: "checkIn" | "checkOut",
    value: string | undefined
  ) {
    setSchedule((cur) => {
      const next = { ...cur };
      const cleaned = cleanDay({ ...(next[day] ?? {}), [field]: value });
      if (cleaned.checkIn === undefined && cleaned.checkOut === undefined) {
        delete next[day];
      } else {
        next[day] = cleaned;
      }
      return next;
    });
  }

  function toggleDay(
    day: DayOfWeek,
    field: "checkIn" | "checkOut",
    enable: boolean
  ) {
    // Enabling = ensure a value exists (default 15:00 checkin / 11:00
    // checkout - common hotel norms). Disabling = strip the field.
    if (!enable) {
      setDay(day, field, undefined);
      return;
    }
    if (schedule[day]?.[field]) return;
    setDay(day, field, field === "checkIn" ? "15:00" : "11:00");
  }

  return (
    <CommitDrawer
      title={t("pmsConfigurationPage.drawerTitle")}
      onClose={onClose}
      width={760}
      data-testid="pms-schedule-drawer"
      onSave={() =>
        onSave({
          dailyResetTime: config.dailyReset ? resetTime : undefined,
          schedule,
        })
      }
    >
      <Stack spacing={3}>
        {config.dailyReset && (
          <Time24Input
            value={resetTime}
            onChange={setResetTime}
            label={t("pmsConfigurationPage.dailyResetAt")}
            testId="pms-daily-reset-time"
          />
        )}
        <Typography variant="caption" color="text.secondary">
          {schedEnabled
            ? t("pmsConfigurationPage.drawerNote")
            : t("pmsConfigurationPage.drawerNoteSwitchedOff")}
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 120, color: "text.secondary" }}>
                {t("pmsConfigurationPage.columns.day")}
              </TableCell>
              <TableCell align="center" sx={{ color: "text.secondary" }}>
                {t("pmsConfigurationPage.columns.checkIn")}
              </TableCell>
              <TableCell align="center" sx={{ color: "text.secondary" }}>
                {t("pmsConfigurationPage.columns.checkOut")}
              </TableCell>
              <TableCell sx={{ width: 160, color: "text.secondary" }}>
                {t("pmsConfigurationPage.columns.checkInTime")}
              </TableCell>
              <TableCell sx={{ width: 160, color: "text.secondary" }}>
                {t("pmsConfigurationPage.columns.checkOutTime")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {DAYS_OF_WEEK.map((day) => {
              const entry = schedule[day] ?? {};
              const cinOn = entry.checkIn !== undefined;
              const coutOn = entry.checkOut !== undefined;
              const label = dayLabel(fmt, day);
              return (
                <TableRow key={day}>
                  <TableCell>{label}</TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={cinOn}
                      onChange={(e) =>
                        toggleDay(day, "checkIn", e.target.checked)
                      }
                      inputProps={
                        {
                          "aria-label": t(
                            "pmsConfigurationPage.enableCheckInFor",
                            {
                              day: label,
                            }
                          ),
                          "data-testid": `pms-${day}-checkin-enabled`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Checkbox
                      size="small"
                      checked={coutOn}
                      onChange={(e) =>
                        toggleDay(day, "checkOut", e.target.checked)
                      }
                      inputProps={
                        {
                          "aria-label": t(
                            "pmsConfigurationPage.enableCheckOutFor",
                            {
                              day: label,
                            }
                          ),
                          "data-testid": `pms-${day}-checkout-enabled`,
                        } as React.InputHTMLAttributes<HTMLInputElement>
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Time24Input
                      value={entry.checkIn ?? ""}
                      placeholder="12:00"
                      onChange={(v) => setDay(day, "checkIn", v)}
                      disabled={!cinOn}
                      testId={`pms-${day}-checkin`}
                    />
                  </TableCell>
                  <TableCell>
                    <Time24Input
                      value={entry.checkOut ?? ""}
                      placeholder="13:00"
                      onChange={(v) => setDay(day, "checkOut", v)}
                      disabled={!coutOn}
                      testId={`pms-${day}-checkout`}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Stack>
    </CommitDrawer>
  );
}

/**
 * 24-hour HH:mm input - a plain text field with pattern enforcement so
 * the displayed format is consistent across browsers (the native
 * `type="time"` control flips to 12h on Safari + macOS-region users).
 */
function Time24Input({
  value,
  onChange,
  disabled,
  label,
  placeholder,
  testId,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  testId: string;
}) {
  const { t } = useTranslation("premises");
  return (
    <TextField
      size="small"
      label={label}
      placeholder={placeholder ?? t("pmsConfigurationPage.timePlaceholder")}
      value={value}
      disabled={disabled}
      onChange={(e) => {
        // Allow live typing - accept anything; we'll normalise on blur.
        onChange(e.target.value);
      }}
      onBlur={(e) => {
        const v = normaliseHHmm(e.target.value);
        if (v !== value) onChange(v);
      }}
      InputLabelProps={{ shrink: true }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start" sx={{ mr: 0.5 }}>
            <AccessTimeIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
      }}
      inputProps={{
        inputMode: "numeric",
        maxLength: 5,
        pattern: "([01][0-9]|2[0-3]):[0-5][0-9]",
        "data-testid": testId,
      }}
      sx={{ width: 150 }}
    />
  );
}

/**
 * Coerce loose input to "HH:mm" 24h format. Accepts "1500", "15:00",
 * "15.00", "9:5" → "09:05". Anything unparseable becomes "".
 */
function normaliseHHmm(input: string): string {
  const digits = input.replace(/[^0-9]/g, "");
  if (digits.length === 0) return "";
  let h = 0;
  let m = 0;
  if (digits.length <= 2) {
    h = Number(digits);
  } else if (digits.length === 3) {
    h = Number(digits.slice(0, 1));
    m = Number(digits.slice(1));
  } else {
    h = Number(digits.slice(0, 2));
    m = Number(digits.slice(2, 4));
  }
  if (!Number.isFinite(h) || !Number.isFinite(m)) return "";
  if (h < 0 || h > 23 || m < 0 || m > 59) return "";
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
